import {
  allowedRouteIds,
  PRIORITY_LABELS,
  STEP_STATUS_LABELS,
  TASK_STATUS_LABELS,
  type DepartmentDto,
  type InboxCountsDto,
  type Page,
  type Priority,
  type RequestTypeDto,
  type RouteDto,
  type TaskStatus,
  type UserDto,
} from '@edms/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export function useUsersList() {
  return useQuery({
    queryKey: ['users', 'picker'],
    queryFn: () => api.get<Page<UserDto>>('/users', { limit: 200 }),
    staleTime: 5 * 60_000,
    select: (p) => p.items,
  });
}

export function useDepartmentsList() {
  return useQuery({ queryKey: ['departments'], queryFn: () => api.get<DepartmentDto[]>('/departments'), staleTime: 5 * 60_000 });
}

/** Маршруты, из которых можно выбрать для заявки этого типа (null — без типа). */
export function useRouteOptions(typeId: string | null, enabled = true) {
  const routes = useQuery({ queryKey: ['routes'], queryFn: () => api.get<RouteDto[]>('/routes'), enabled });
  const types = useQuery({ queryKey: ['request-types'], queryFn: () => api.get<RequestTypeDto[]>('/request-types'), enabled });
  if (!routes.data || !types.data) return [];
  const type = typeId ? (types.data.find((t) => t.id === typeId) ?? null) : null;
  const ids = allowedRouteIds(type, types.data, routes.data.map((r) => r.id));
  return ids.map((id) => routes.data.find((r) => r.id === id)!).filter(Boolean);
}

export function useInboxCounts() {
  return useQuery({
    queryKey: ['tasks', 'counts'],
    queryFn: () => api.get<InboxCountsDto>('/tasks/counts'),
    refetchInterval: 60_000,
  });
}

/** Выбор сотрудника: список активных, по алфавиту. */
export function UserSelect({
  value,
  onChange,
  exclude = [],
  placeholder = 'Выберите сотрудника',
  id,
  invalid,
}: {
  value: string;
  onChange: (id: string) => void;
  exclude?: string[];
  placeholder?: string;
  id?: string;
  invalid?: boolean;
}) {
  const users = useUsersList();
  return (
    <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={invalid}>
      <option value="">{users.isPending ? 'Загрузка…' : placeholder}</option>
      {users.data
        ?.filter((u) => !exclude.includes(u.id) || u.id === value)
        .map((u) => (
          <option key={u.id} value={u.id}>
            {u.fullName}
            {u.position ? ` — ${u.position}` : ''}
          </option>
        ))}
    </select>
  );
}

const STATUS_TONE: Record<string, string> = {
  approved: 'badge-brand',
  done: 'badge-brand',
  returned: 'badge-warn',
  answered: 'badge-warn',
  draft: '',
  rejected: 'badge-danger',
  cancelled: 'badge-danger',
  in_approval: 'badge-info',
  open: 'badge-info',
};

export function StatusBadge({ status }: { status: TaskStatus }) {
  return (
    <span className={`badge ${STATUS_TONE[status] ?? ''}`}>
      <span className="dot" />
      {TASK_STATUS_LABELS[status]}
    </span>
  );
}

const STEP_TONE: Record<string, string> = {
  approved: 'badge-brand',
  declined: 'badge-warn',
  rejected: 'badge-danger',
  pending: 'badge-info',
  interrupted: '',
  skipped: '',
  waiting: '',
};

/** Для задач (скан, регистрация) — свои подписи: «Выполняет», «Выполнено». */
const TASK_STATUS_TEXT: Record<string, string> = { pending: 'Выполняет', approved: 'Выполнено' };

export function StepBadge({ status, kind }: { status: string; kind?: 'approve' | 'task' }) {
  const text = (kind === 'task' && TASK_STATUS_TEXT[status]) || STEP_STATUS_LABELS[status] || status;
  return <span className={`badge ${STEP_TONE[status] ?? ''}`}>{text}</span>;
}

export function PriorityMark({ priority }: { priority: Priority }) {
  if (priority === 'normal') return null;
  const tone = priority === 'urgent' ? 'badge-danger' : priority === 'high' ? 'badge-warn' : '';
  return <span className={`badge ${tone}`}>{PRIORITY_LABELS[priority]}</span>;
}

const dueFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function Due({ at, done, prefix }: { at: string | null; done?: boolean; prefix?: string }) {
  if (!at) return <span className="faint">без срока</span>;
  const overdue = !done && new Date(at).getTime() < Date.now();
  return (
    <span className={overdue ? 'overdue' : 'faint'} title={overdue ? 'Срок прошёл' : 'Срок'}>
      {overdue ? '⚠ ' : ''}
      {prefix ? `${prefix} ` : ''}
      {dueFmt.format(new Date(at))}
    </span>
  );
}

/** Галочки как в мессенджере: одна — пришло, две — открыл после этого. */
export function Ticks({ activatedAt, seenAt }: { activatedAt: string | null; seenAt: string | null }) {
  if (!activatedAt) return null;
  const read = Boolean(seenAt && new Date(seenAt).getTime() >= new Date(activatedAt).getTime());
  return (
    <span className={`ticks${read ? ' read' : ''}`} title={read ? `Открыл(а): ${dueFmt.format(new Date(seenAt!))}` : 'Доставлено, ещё не открывал(а)'}>
      {read ? '✓✓' : '✓'}
    </span>
  );
}

/** Подсказка к сроку документа: рабочие сроки задаёт маршрут. */
export const DOC_DUE_HINT = 'Ориентир для всех: к какой дате нужен результат. Сроки участникам задаёт маршрут — часы на каждый шаг, по ним приходят напоминания.';

/** datetime-local ↔ ISO */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}
